/**
 * features/payments/paymentsWrite.ts
 * ------------------------------------------------------------------
 * Record a payment against an issued invoice. A gapless receipt
 * number is reserved from the RCT series, then a single transaction
 * both writes the payment and rolls the invoice's amountPaid /
 * balance / status (part_paid → paid) so the two never drift.
 * ------------------------------------------------------------------
 */
import { collection, doc, runTransaction } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { nextNumber } from '@/lib/numbering';
import { round2 } from '@/lib/money';
import { COLLECTIONS, type Invoice, type PaymentMethod } from '@/types';

export interface PaymentInput {
  amount: number;
  date: string; // yyyy-mm-dd
  method: PaymentMethod;
  reference?: string;
  notes?: string;
}

export async function recordPayment(
  invoice: Invoice,
  input: PaymentInput,
  actor: string,
): Promise<{ receiptNumber: string }> {
  if (!(input.amount > 0)) throw new Error('Enter an amount greater than zero.');

  // Reserve the receipt number first (its own atomic counter txn).
  const receiptNumber = await nextNumber('receipt', new Date(input.date));
  const now = new Date().toISOString();
  const invoiceRef = doc(db, COLLECTIONS.invoices, invoice.id);
  const paymentRef = doc(collection(db, COLLECTIONS.payments));

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(invoiceRef);
    if (!snap.exists()) throw new Error('Invoice not found.');
    const inv = snap.data() as Omit<Invoice, 'id'>;
    if (inv.status === 'void') throw new Error('Cannot record a payment against a void invoice.');
    if (inv.number == null) throw new Error('Issue the invoice before recording a payment.');

    const newPaid = round2((inv.amountPaid ?? 0) + input.amount);
    const newBalance = round2(inv.totals.total - newPaid);
    const status: Invoice['status'] = newBalance <= 0 ? 'paid' : 'part_paid';

    tx.update(invoiceRef, {
      amountPaid: newPaid,
      balance: newBalance,
      status,
      updatedAt: now,
    });

    const payment: Record<string, unknown> = {
      invoiceId: invoice.id,
      invoiceNumber: inv.number,
      clientId: inv.clientId,
      clientName: inv.client.name,
      amount: round2(input.amount),
      date: input.date,
      method: input.method,
      receiptNumber,
      createdAt: now,
      createdBy: actor,
    };
    const ref = input.reference?.trim();
    if (ref) payment.reference = ref;
    const notes = input.notes?.trim();
    if (notes) payment.notes = notes;

    tx.set(paymentRef, payment);
  });

  return { receiptNumber };
}
